import {
  HStack,
  Radio,
  RadioGroup,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import React, { useState } from 'react';

import { Market } from '../../types/shared/Market';
import {
  MarketDates,
  Field,
  DateSelection,
  DateList,
  PendingFieldChange,
} from '../../types/shared/Vendor';
import { DateStatus } from '../../runtypes/VendorRuntype';
import formatDate from '../../core/support/formatDate';
import { ListEmptyState } from '../../core-ui';

type Props = {
  field: Field;
  pendingChange: PendingFieldChange;
  markets: Array<Market>;
  onApprovalChange: (
    marketId: string,
    date: string,
    approvalStatus: ApprovalStatus,
  ) => void;
};

type DateRequestStatus = 'REQUEST_ADD' | 'REQUEST_REMOVE';

const ApprovalStatuses = toEnum({
  APPROVED: true,
  DENIED: true,
  TO_WAITLIST: true,
});
export type ApprovalStatus = keyof typeof ApprovalStatuses;
const defaultApprovalStatus: ApprovalStatus = 'DENIED';

export function ChangeRequestReviewMarketsView(props: Props) {
  const { field, pendingChange, markets, onApprovalChange } = props;

  const oldMarketDates = (pendingChange?.oldValue ?? {}) as MarketDates;
  const newMarketDates = (pendingChange?.newValue ?? {}) as MarketDates;
  const dateSelectionByMarket = field.dateSelectionByMarket ?? {};

  const renderedMarkets = markets.map((market) => {
    return renderMarketView({
      market,
      dateSelection: dateSelectionByMarket[market.id] ?? {},
      oldDates: oldMarketDates[market.id] ?? [],
      newDates: newMarketDates[market.id] ?? [],
      onApprovalChange: (date, approvalStatus) =>
        onApprovalChange(market.id, date, approvalStatus),
    });
  });

  const hasAny = renderedMarkets.some((el) => el != null);

  if (!hasAny) {
    return (
      <VStack align="stretch">
        <ListEmptyState message={t('No changes to market dates')} />
      </VStack>
    );
  }

  return <VStack align="stretch">{renderedMarkets}</VStack>;
}

function renderMarketView(props: {
  market: Market;
  dateSelection: DateSelection;
  oldDates: Array<string>;
  newDates: Array<string>;
  onApprovalChange: (date: string, approvalStatus: ApprovalStatus) => void;
}) {
  const { market, dateSelection, oldDates, newDates, onApprovalChange } = props;

  const changeReqByDate = computeMarketDates(
    market,
    dateSelection,
    oldDates,
    newDates,
  );

  const renderedDates = market.dates.map((date) => {
    const changeReq = changeReqByDate[date];
    if (changeReq === undefined) {
      return null;
    }
    return (
      <DateView
        key={date}
        date={date}
        currentStatus={dateSelection[date]}
        request={changeReq}
        onApprovalChange={onApprovalChange}
      />
    );
  });

  const hasAny = renderedDates.some((el) => el != null);
  if (!hasAny) {
    return null;
  }

  // TODO: Use a table for the dates
  return (
    <VStack key={market.id} align="stretch">
      <Text>{market.name}</Text>
      <VStack align="stretch">{renderedDates}</VStack>
    </VStack>
  );
}

function DateView(props: {
  date: string;
  currentStatus: DateStatus | undefined;
  request: DateRequestStatus;
  onApprovalChange: (date: string, approvalStatus: ApprovalStatus) => void;
}) {
  const { date, currentStatus, request, onApprovalChange } = props;
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatus>(
    defaultApprovalStatus,
  );
  return (
    <HStack spacing={4}>
      <Text whiteSpace="nowrap">{formatDate(date, { fromUTC: true })}</Text>
      <Text>
        {
          // TODO: Add color/icon; better t()
          t('Current status: {status}', {
            status: getLabelForDateStatus(currentStatus),
          })
        }
      </Text>
      <Text>
        {
          // TODO: Add color/icon
          request === 'REQUEST_ADD' ? t('Request: Add') : t('Request: Remove')
        }
      </Text>
      <Spacer />
      <RadioGroup
        onChange={(value) => {
          const lookup: Record<string, ApprovalStatus> = ApprovalStatuses;
          const approvalStatus = lookup[value] ?? defaultApprovalStatus;
          setApprovalStatus(approvalStatus);
          onApprovalChange(date, approvalStatus);
        }}
        value={approvalStatus}
      >
        <HStack>
          <Radio value={ApprovalStatuses.DENIED}>{t('Reject')}</Radio>
          <Radio value={ApprovalStatuses.APPROVED}>{t('Approve')}</Radio>
          {request === 'REQUEST_ADD' ? (
            <Radio value={ApprovalStatuses.TO_WAITLIST}>{t('Waitlist')}</Radio>
          ) : null}
        </HStack>
      </RadioGroup>
    </HStack>
  );
}

// Note: Duplicate logic here and in DateStatusText and MarketsView
function getLabelForDateStatus(dateStatus: DateStatus | undefined) {
  switch (dateStatus) {
    case 'REQUESTED': {
      return t('Requested');
    }
    case 'APPROVED': {
      return t('Approved');
    }
    case 'WAITLISTED': {
      return t('Waitlisted');
    }
    default: {
      return t('Not Attending');
    }
  }
}

function computeMarketDates(
  market: Market,
  dateSelection: DateSelection,
  oldDates: DateList,
  newDates: DateList,
) {
  const { added, removed } = diffSets(new Set(oldDates), new Set(newDates));
  const result: { [date: string]: DateRequestStatus } = {};
  for (const date of market.dates) {
    const status = dateSelection[date];
    if (added.has(date)) {
      // Can't add the date if already added
      if (status !== 'APPROVED') {
        result[date] = 'REQUEST_ADD';
      }
    }
    if (removed.has(date)) {
      // Can't remove the date if not currently added
      if (status !== undefined) {
        result[date] = 'REQUEST_REMOVE';
      }
    }
  }
  return result;
}

function diffSets<T>(before: Set<T>, after: Set<T>) {
  const added = new Set<T>();
  const removed = new Set<T>();
  for (const item of after) {
    if (!before.has(item)) {
      added.add(item);
    }
  }
  for (const item of before) {
    if (!after.has(item)) {
      removed.add(item);
    }
  }
  return { added, removed };
}

// TODO: Move this
function toEnum<T extends string>(obj: Record<T, true>) {
  const mapped = Object.fromEntries(Object.keys(obj).map((key) => [key, key]));
  return mapped as { [K in T]: K };
}
